(() => {
  const identity = (item) => JSON.stringify([
    Number(item.variant_id || item.id),
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
    item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null,
  ]);
  const restoreItem = (item, quantity = item.quantity) => {
    const result = { id: Number(item.variant_id || item.id), quantity, properties: item.properties || {} };
    const plan = item.selling_plan_allocation?.selling_plan?.id;
    if (plan) result.selling_plan = plan;
    return result;
  };
  const findLine = (cart, item) => cart.items.find((line) => identity(line) === identity(item));
  const isGift = (item) => {
    if (Number(item.product_id) === 9144023220530) return true;
    if (Number(item.product_id) !== 8375637836082) return false;
    try { return JSON.parse(item.properties?._kaching_cart || '{}').promoHash === 'product-discount-90-percentage-100'; } catch { return false; }
  };
  if (typeof module !== 'undefined') module.exports = { identity, restoreItem, findLine, isGift };
  if (typeof HTMLElement === 'undefined') return;

  class MaresiCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = window.Shopify?.routes?.root || '/';
      this.version = 0;
      this.busy = false;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        if (button.dataset.action === 'close') return this.close();
        if (button.dataset.action === 'offer-prev' || button.dataset.action === 'offer-next') {
          const offers = this.querySelector('.mc-offers');
          offers.scrollBy({ left: offers.clientWidth * (button.dataset.action === 'offer-next' ? 1 : -1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          return;
        }
        this.mutate(button.dataset.action, button);
      });
      this.addEventListener('change', (event) => {
        const select = event.target;
        if (select.matches('[data-line-variant]')) this.mutate('variant', select);
        if (select.matches('[data-offer-variant]')) {
          select.closest('[data-offer]').querySelectorAll('[data-offer-price]').forEach((price) => {
            price.hidden = price.dataset.offerPrice !== select.value;
          });
        }
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('#cart-icon-bubble, [data-cart-icon], [data-cart-toggle]');
        if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey) return;
        event.preventDefault();
        this.setActiveElement(trigger);
        this.renderContents();
      });
      document.addEventListener('cart:refresh', () => this.renderContents());
      document.addEventListener('kaching-cart:open', (event) => {
        this.setActiveElement(event.detail?.element);
        this.renderContents();
      });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh().catch((error) => this.message(error.message));
      });
      this.convert();
    }

    setActiveElement(element) { this.activeElement = element || document.activeElement; }
    getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; }
    getSectionDOM(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector); }
    getSectionInnerHTML(html, selector = '.shopify-section') { return this.getSectionDOM(html, selector)?.innerHTML || ''; }
    state() { return JSON.parse(this.querySelector('[data-cart-state]').textContent); }
    track(kind, details = {}, cart) {
      try {
        if (typeof window === 'undefined' || !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() || !window.Shopify?.analytics?.publish) return false;
        const token = (cart || this.state()).token?.split('?')[0];
        if (!token || !['production', 'preview'].includes(this.dataset.analyticsEnvironment)) return false;
        const result = window.Shopify.analytics.publish('maresi_cart_event', {
          ...details, id: crypto.randomUUID(), kind, cartToken: token,
          environment: this.dataset.analyticsEnvironment, device: matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop',
        });
        result?.catch?.(() => {});
        return true;
      } catch { return false; }
    }
    watchOffers() {
      this.offerObserver?.disconnect();
      if (!this.classList.contains('is-open') || typeof IntersectionObserver === 'undefined') return;
      this.offerObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || !this.classList.contains('is-open')) continue;
          const productId = entry.target.querySelector('[data-product]')?.dataset.product;
          if (productId && !this.seenOffers.has(productId) && this.track('offer_view', { offerId: productId, productId })) this.seenOffers.add(productId);
        }
      }, { threshold: 0.5 });
      this.querySelectorAll('[data-offer]').forEach(offer => this.offerObserver.observe(offer));
    }
    message(text) {
      this.querySelector('.mc-message').textContent = text;
      this.querySelector('.mc-view-cart').hidden = !text;
    }
    convert() {
      // BUCKS owns rates, geolocation and the shopper's saved currency choice.
      try { window.bucksCC?.reConvert(); } catch (error) { console.warn('Maresi cart: BUCKS conversion unavailable.', error); }
    }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.setActiveElement(trigger || this.activeElement);
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      this.pendingAnalyticsOpen = true;
      this.seenOffers = new Set();
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const dialog = this.querySelector('.mc-dialog');
      trapFocus(dialog, this.querySelector('button[data-action="close"]'));
      document.dispatchEvent(new CustomEvent('cart:opened'));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.pendingAnalyticsOpen = false;
      this.offerObserver?.disconnect();
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      document.body.style.overflow = this.previousOverflow;
      removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble, [data-cart-icon]'));
    }
    async request(path, body) {
      const response = await fetch(`${this.root}${path}`, {
        credentials: 'same-origin', cache: 'no-store',
        ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'Your bag could not be updated. Please try again.');
      return data;
    }
    cartFromResponse(response) {
      if (Array.isArray(response?.items) && Number.isFinite(response.total_price)) return response;
      try {
        const source = response?.sections?.['cart-drawer'] && this.getSectionDOM(response.sections['cart-drawer'], '#CartDrawer');
        const cart = JSON.parse(source?.querySelector('[data-cart-state]')?.textContent || 'null');
        if (Array.isArray(cart?.items) && Number.isFinite(cart.total_price)) return { ...cart, sections: response.sections };
      } catch { /* Missing or invalid sections fall back to a fresh cart read. */ }
      return null;
    }
    async cartRequest(path, body) {
      const response = await this.request(path, {
        ...body, sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: typeof window === 'undefined' ? '/' : window.location.pathname,
      });
      return this.cartFromResponse(response) || response;
    }
    hasSections(sections) {
      if (typeof sections?.['cart-drawer'] !== 'string' || typeof sections?.['cart-icon-bubble'] !== 'string') return false;
      try { return !!this.getSectionDOM(sections['cart-drawer'], '#CartDrawer')?.querySelector('[data-cart-state]'); } catch { return false; }
    }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('button:not([data-action="close"]), select').forEach((control) => {
        if (value) { if (control.dataset.wasDisabled === undefined) control.dataset.wasDisabled = String(control.disabled); control.disabled = true; }
        else if (control.dataset.wasDisabled !== undefined) { control.disabled = control.dataset.wasDisabled === 'true'; delete control.dataset.wasDisabled; }
      });
      if (this.checkoutBlocked) this.querySelector('.mc-checkout')?.setAttribute('disabled', '');
    }
    async refresh(response, reveal = false) {
      const version = ++this.version;
      if (!this.refreshDepth) this.refreshWasBusy = this.busy;
      this.refreshDepth = (this.refreshDepth || 0) + 1;
      this.checkoutBlocked = true;
      this.setBusy(true);
      try {
        // A response that predates an in-flight reward update must be read again.
        const pending = this.rewardSync;
        if (pending) await pending;
        if (version !== this.version) return false;
        const initial = pending ? null : this.cartFromResponse(response);
        if (this.hasSections(initial?.sections)) {
          this.paintSections(initial.sections);
          if (reveal) this.showContents();
        }
        // Existing merchant discounts still run through Kaching's installed Shopify Functions.
        const sync = this.syncGift(initial);
        this.rewardSync = sync;
        let cart;
        try { cart = await sync; } finally { if (this.rewardSync === sync) this.rewardSync = null; }
        if (version !== this.version) return false;
        this.checkoutBlocked = false;
        const sections = this.hasSections(cart?.sections) ? cart.sections : await this.request('?sections=cart-drawer,cart-icon-bubble');
        if (version !== this.version) return false;
        if (sections !== initial?.sections) this.paintSections(sections);
        return true;
      } finally {
        this.refreshDepth--;
        if (!this.refreshDepth && !this.refreshWasBusy) this.setBusy(false);
      }
    }
    paintSections(sections) {
      const source = sections['cart-drawer'] && this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
      if (!source?.querySelector('[data-cart-state]')) throw new Error('Your bag changed, but the drawer could not refresh. Use View full cart to check it.');
      const focused = this.quantityFocus || document.activeElement;
      const focusKey = focused?.dataset.key;
      const focusAction = focused?.dataset.action;
      const focusQuantityDirection = focused?.dataset.quantityDirection;
      const lineVariant = focused?.matches('[data-line-variant]');
      const contentHadFocus = !!this.quantityFocus || this.querySelector('#CartDrawer').contains(focused);
      const editing = [...this.querySelectorAll('.mc-edit[open]')].map((element) => element.dataset.key);
      const stackExpanded = this.querySelector('.mc-bundle-details')?.open;
      const offerIndex = Math.round((this.querySelector('.mc-offers')?.scrollLeft || 0) / (this.querySelector('.mc-offers')?.clientWidth || 1));
      this.querySelector('#CartDrawer').innerHTML = source.innerHTML;
      this.querySelectorAll('.mc-edit').forEach((element) => { element.open = editing.includes(element.dataset.key); });
      const stackDetails = this.querySelector('.mc-bundle-details');
      if (stackDetails) stackDetails.open = !!stackExpanded;
      const offers = this.querySelector('.mc-offers');
      if (offers) offers.scrollTo({ left: offerIndex * offers.clientWidth, behavior: 'instant' });
      const bubble = document.querySelector('#cart-icon-bubble');
      const bubbleSource = sections['cart-icon-bubble'] && this.getSectionDOM(sections['cart-icon-bubble']);
      if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
      if (this.busy) this.setBusy(true);
      this.convert();
      if (this.rewardWarning) this.message(this.rewardWarning);
      if (this.classList.contains('is-open')) {
        if (this.pendingAnalyticsOpen) { this.track('cart_open'); this.pendingAnalyticsOpen = false; }
        this.watchOffers();
        const refreshedFocusKey = this.quantityFocusItem ? findLine(this.state(), this.quantityFocusItem)?.key || focusKey : focusKey;
        const target = [...this.querySelectorAll('[data-key]')].find((element) => element.dataset.key === refreshedFocusKey && (focused?.tagName === 'SUMMARY' ? element.tagName === 'SUMMARY' : lineVariant ? element.matches('[data-line-variant]') : element.dataset.action === focusAction && (!focusQuantityDirection || element.dataset.quantityDirection === focusQuantityDirection)));
        this.pendingFocus = contentHadFocus ? target : focused?.isConnected && this.contains(focused) ? focused : null;
        trapFocus(this.querySelector('.mc-dialog'), this.pendingFocus && !this.pendingFocus.disabled ? this.pendingFocus : this.querySelector('button[data-action="close"]'));
      }
    }
    showContents() {
      this.open();
      if (this.pendingAnalyticsOpen) { this.track('cart_open'); this.pendingAnalyticsOpen = false; }
      this.watchOffers();
    }
    async renderContents(response) {
      try {
        if (await this.refresh(response, true) === false) return;
        this.showContents();
      } catch (error) {
        this.open();
        this.message(error.message);
      }
    }
    async syncGift(initial) {
      let cart = initial || await this.request('cart.js');
      const gifts = cart.items.filter(isGift);
      // Replace legacy gifts and normalize the reward to one correctly marked gift.
      if (gifts.length > 1 || gifts.some(item => Number(item.product_id) !== 9144023220530 || item.quantity !== 1 || item.properties?._maresi_gift !== 'true' || !item.properties?._kaching_cart?.includes('product-discount-90-percentage-100'))) {
        cart = await this.cartRequest('cart/update.js', { updates: Object.fromEntries(gifts.map(item => [item.key, 0])) });
      }
      // Count paid merchandise after line discounts, excluding the gift.
      const total = cart.items.filter(item => !isGift(item)).reduce((sum, item) => sum + item.final_line_price, 0);
      const eligible = total >= 9000;
      if (eligible && !this.giftEligible) this.track('gift_eligible', {}, cart);
      this.giftEligible = eligible;
      if (cart.currency !== 'USD') throw new Error('Please use the full cart to confirm reward eligibility in this currency.');
      if (total < 9000 && cart.items.some(isGift)) {
        cart = await this.cartRequest('cart/update.js', { updates: Object.fromEntries(cart.items.filter(isGift).map(item => [item.key, 0])) });
      }
      const attributes = {
        cartConfigId: 'f51788e0-e4d1-4666-a1cd-7edf43aa6d25',
        marketId: Number(this.dataset.market), marketCurrencyCode: 'USD',
        totalAfterDiscounts: total, subtotalAtPublish: cart.items_subtotal_price,
        originalSubtotalAtPublish: cart.original_total_price,
      };
      const serialized = JSON.stringify(attributes);
      if (cart.attributes?._kaching_cart_attributes !== serialized) {
        cart = await this.cartRequest('cart/update.js', { attributes: { _kaching_cart_attributes: serialized } });
      }
      if (total < 9000 || this.dismissedGiftTotal === total || this.failedGiftTotal === total) return cart;
      const addedGift = !cart.items.some(isGift);
      if (addedGift) {
        const added = await this.cartRequest('cart/add.js', { items: [{ id: 48151618158898, quantity: 1, properties: {
          _maresi_gift: 'true', _kaching_cart: JSON.stringify({ promoHash: 'product-discount-90-percentage-100', promoRewardUniqueId: crypto.randomUUID() }),
        } }] });
        cart = this.cartFromResponse(added) || await this.request('cart.js');
      }
      const chargedGifts = cart.items.filter(item => isGift(item) && item.final_line_price > 0);
      if (chargedGifts.length) {
        this.failedGiftTotal = total;
        cart = await this.cartRequest('cart/update.js', { updates: Object.fromEntries(chargedGifts.map(item => [item.key, 0])) });
        this.rewardWarning = 'The free bracelet discount could not be applied. The gift was removed so you will not be charged for it.';
        this.track('cart_error', { errorCode: 'gift_discount_failed' }, cart);
      } else {
        this.rewardWarning = '';
        if (addedGift && cart.items.some(isGift)) this.track('gift_added', {}, cart);
      }
      return cart;
    }
    async replace(line, variantId, productId, upgrade) {
      if (line.selling_plan_allocation) throw new Error('Please change this subscription from the full cart.');
      if (upgrade && Object.keys(line.properties || {}).length) throw new Error('Customized bracelets must be changed from their product page.');
      const before = await this.request('cart.js');
      const source = findLine(before, line);
      if (!source) throw new Error('This bracelet has changed. Please review your bag again.');
      if (upgrade && before.items.some((item) => Number(item.product_id) === productId)) throw new Error('This set is already in your bag.');
      const quantity = upgrade ? 1 : source.quantity;
      // Add first so an unavailable replacement can never remove the original bracelet.
      await this.request('cart/add.js', { items: [{ id: variantId, quantity, properties: upgrade ? {} : source.properties || {} }] });
      try {
        const after = await this.request('cart.js');
        const current = findLine(after, source);
        if (!current || current.quantity !== source.quantity) throw new Error('The original line changed.');
        await this.request('cart/change.js', { id: current.key, quantity: source.quantity - quantity });
      } catch (error) {
        throw new Error('The replacement was added, but we could not confirm removal of the original. Review both items in your bag before checkout.');
      }
    }
    async removeStack(returnCart = false) {
      const current = await this.request('cart.js');
      const items = current.items.filter(item => item.properties?._bracelet_stack === 'true');
      if (!items.length) throw new Error('This bundle has changed. Please review your bag.');
      const updated = await this.cartRequest('cart/update.js', { updates: Object.fromEntries(items.map(item => [item.key, 0])) });
      return returnCart ? updated : items.map(item => restoreItem(item));
    }
    async mutate(action, control) {
      if (this.busy) return;
      this.pendingFocus = null;
      this.quantityFocus = action === 'quantity' ? control : null;
      this.setBusy(true);
      this.message('');
      let errorMessage = '';
      let updatedCart;
      try {
        const cart = this.state();
        const line = cart.items.find((item) => item.key === control.dataset.key);
        this.quantityFocusItem = action === 'quantity' ? line : null;
        if (['quantity', 'remove', 'variant', 'upgrade'].includes(action) && !line) throw new Error('This item has changed. Please review your bag.');
        if (action === 'remove-stack') {
          updatedCart = await this.removeStack(true);
        } else if (action === 'remove' || action === 'quantity') {
          const quantity = action === 'remove' ? 0 : Number(control.dataset.quantity);
          if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Choose a valid quantity.');
          updatedCart = await this.cartRequest('cart/change.js', { id: line.key, quantity });
          if (action === 'remove' && isGift(line)) this.dismissedGiftTotal = cart.items.filter(item => !isGift(item)).reduce((sum, item) => sum + item.final_line_price, 0);
        } else if (action === 'variant') {
          const id = Number(control.value);
          if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Choose an available variant.');
          if (id !== Number(line.variant_id || line.id)) await this.replace(line, id, 0, false);
        } else if (action === 'upgrade' || action === 'add') {
          const id = Number(control.dataset.variant || control.closest('[data-offer]').querySelector('[data-offer-variant]')?.value);
          const productId = Number(control.dataset.product);
          if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Choose an available variant.');
          if (action === 'upgrade') await this.replace(line, id, productId, true);
          else {
            const current = await this.request('cart.js');
            if (current.items.some((item) => Number(item.product_id) === productId)) throw new Error('This bracelet is already in your bag.');
            const properties = control.dataset.offerHash ? { _kaching_cart: JSON.stringify({ isUpsellProduct: true, upsellProductHashPayload: { discountAmount: 10, discountType: 'percentage', hash: control.dataset.offerHash, cartMode: 'active' } }) } : {};
            properties._maresi_offer = String(productId);
            updatedCart = await this.cartRequest('cart/add.js', { items: [{ id, quantity: 1, properties }] });
            this.track('offer_added', { offerId: String(productId), productId: String(productId) }, current);
          }
        }
      } catch (error) {
        errorMessage = error instanceof TypeError ? 'Connection interrupted. Your bag may have changed; check it before trying again.' : error.message;
      } finally {
        try { await this.refresh(updatedCart); } catch (error) { errorMessage = errorMessage ? `${errorMessage} ${error.message}` : error.message; }
        this.setBusy(false);
        this.message(errorMessage || this.rewardWarning || '');
        if (errorMessage) this.track('cart_error', { errorCode: action === 'add' ? 'offer_add_failed' : 'cart_update_failed' });
        if (this.classList.contains('is-open')) trapFocus(this.querySelector('.mc-dialog'), this.pendingFocus?.isConnected ? this.pendingFocus : this.querySelector('button[data-action="close"]'));
        this.quantityFocus = null;
        this.quantityFocusItem = null;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'maresi-cart', cart: this.state() } }));
      }
    }
  }
  customElements.define('cart-drawer', MaresiCart);
})();

